/**
 * Tournament types for the campaigns system.
 * These types were migrated from features/tournaments to make campaigns self-contained.
 */

import type IGameSettings from '../../../../types/models/IGameSettings';
import type { GET_PRIZE_TEXT_GIFT_SPIN, GET_PRIZE_TEXT_MONEY, GET_PRIZE_TEXT_ITEM } from '../../../../constants';
import type { CampaignLevel, CampaignTypes, ICampaignResources } from '../campaignShared.types';
import type { ICampaignInfo } from '../campaign.types';

export const TOURNAMENTS_CAMPAIGN_TYPE: Extract<CampaignTypes, 'tournaments'> = 'tournaments';
// fallow-ignore-next-line unused-export
export const TOURNAMENTS_DISPLAY_NAME = 'Tournament';

export type TournamentPrizeType =
  | typeof GET_PRIZE_TEXT_GIFT_SPIN
  | typeof GET_PRIZE_TEXT_ITEM
  | typeof GET_PRIZE_TEXT_MONEY;

export type PrizeGiftSpinConfig = {
  games: string[];
  spinCount: number;
  bet: number | null;
};

export interface ITournamentPrize {
  name: string;
  place: number;
  type: TournamentPrizeType;
  currencyCode: string;
  positionRange?: string;
  giftSpinConfig?: PrizeGiftSpinConfig;
}

export interface ITournamentScoreBoard {
  name: string;
  casinoId: number;
  score: number;
  position: number;
}

export enum IGameModes {
  BUY_BONUS = 'BUY_BONUS',
}

export interface IEligibleGameModes {
  eligibleGameModes: IGameModes[] | null;
}

export interface IPlayerScore extends ITournamentScoreBoard {
  selected?: boolean;
}

export enum TournamentStatuses {
  PENDING = 'PENDING',
  PROMO = 'PROMO',
  ACTIVE = 'ACTIVE',
  AWARDING = 'AWARDING',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  STOPPED = 'STOPPED',
  FINISHED = 'FINISHED',
  FINAL = 'FINAL',
  AWARDING_VISIBLE = 'AWARDING_VISIBLE',
  COMPLETED_VISIBLE = 'COMPLETED_VISIBLE',
}

/** Priority order shared across tournaments */
export const TOURNAMENT_STATUS_ORDER: Record<TournamentStatuses, number> = {
  [TournamentStatuses.ACTIVE]: 1,
  [TournamentStatuses.PROMO]: 2,
  [TournamentStatuses.PENDING]: 3,
  [TournamentStatuses.AWARDING]: 4,
  [TournamentStatuses.COMPLETED]: 5,
  [TournamentStatuses.CANCELLED]: 6,
  [TournamentStatuses.STOPPED]: 7,
  [TournamentStatuses.FINAL]: 8,
  [TournamentStatuses.AWARDING_VISIBLE]: 9,
  [TournamentStatuses.COMPLETED_VISIBLE]: 10,
  [TournamentStatuses.FINISHED]: 11,
};

export enum TournamentFormulas {
  WINNINGS_PLUS_NUMBER_OF_SPINS = "WINNINGS_PLUS_NUMBER_OF_SPINS",
  WINNINGS_IN_COINS_PLUS_NUMBER_OF_SPINS = "WINNINGS_IN_COINS_PLUS_NUMBER_OF_SPINS",
  BET_IN_COINS = "BET_IN_COINS",
  WINNINGS_D_BETS = "WINNINGS_D_BETS",
  WINNINGS_D_BETS_X_100 = "WINNINGS_D_BETS_X_100",
  WINNINGS_PLUS_BETS = "WINNINGS_PLUS_BETS",
  WINNINGS_X_5_PLUS_NUMBER_OF_SPINS = "WINNINGS_X_5_PLUS_NUMBER_OF_SPINS",
  BET_IN_COINS_COEF = "BET_IN_COINS_COEF",
  WINNINGS_PLUS_BETS_D_100 = "WINNINGS_PLUS_BETS_D_100",
  WINNINGS_X_5_PLUS_BETS = "WINNINGS_X_5_PLUS_BETS",
  WINNINGS_D_BETS_X_100_PLUS_SPINS = "WINNINGS_D_BETS_X_100_PLUS_SPINS"
}

/**
 * Tournaments use the shared `Campaigns*` resource vocabulary from ICampaignResources
*/
export interface ITournamentResources extends ICampaignResources {}

/** Tournament card props */
export interface ITournamentCampaignInfo extends Omit<ICampaignInfo, 'component'> {
  prizeFund: number;
  resources: ITournamentResources;
}

export interface ITournament {
  id: string;
  name: string;
  type: typeof TOURNAMENTS_CAMPAIGN_TYPE;
  logoUrl: string | null;
  description: string | null;
  terms: string;
  status: TournamentStatuses;
  joined: boolean;
  promoStartDate: string;
  promoEndDate: string;
  startDate: string;
  endDate: string;
  prizes: ITournamentPrize[];
  prizeFund: number;
  totalPrizePool?: number;
  currency?: string;
  level: CampaignLevel;
  scoreBoard: ITournamentScoreBoard[];
  joinedPlayersCount: number;
  currentPlayersCount?: number;
  maxPlayersCount?: number;
  score: IPlayerScore;
  games: string[];
  showWelcomeModal: boolean;
  autoJoin: boolean;
  resources: ITournamentResources;
  baseCurrency: string;
  restrictions: IEligibleGameModes;
}

export interface IParsedTournament extends Omit<ITournament, 'games'> {
  gamesKeys: { [key: string]: string };
  games: IGameSettings[];
  minSpinNumber: number;
  minStake: number;
  minBet?: number;
  baseCurrency: string;
  formula: TournamentFormulas;
  formulaParams: IFormulaParams;
  autoJoin: boolean;
}

export interface IFormulaParams {
  coef: number;
}

export interface ITournamentSessionData {
  gameKey?: string;
  sessionKey: string;
  playerId: string;
}

export interface IJoinTournamentSessionData extends Omit<ITournamentSessionData, 'gameKey'> {
  tournamentIds: string[];
}

export interface IJoinTournamentProps {
  host: string;
  data: IJoinTournamentSessionData;
}

export type IJoinError =
  | 401 | 402 | 403 | 404 | 405 | 406 | 407 | 408 | 409;

export interface IJoinTournamentResponse {
  id: string;
  errorCode: IJoinError | null;
  joined: boolean;
}

export interface IGetLeaderboardDataProps {
  host: string;
  tournamentId: string;
  tournamentSessionInfo: ITournamentSessionData;
}

export interface ITournamentAPIResponse {
  autoJoin: boolean;
  tournaments: ITournament[];
  playerName: string;
}

export interface ILeaderboard {
  joinedPlayersCount: number;
  status: TournamentStatuses;
  score: IPlayerScore;
  scoreBoard: ITournamentScoreBoard[];
  tournamentId: string;
  totalActivePlayers?: number;
  total_active_players?: number;
}
